import React, { useEffect } from 'react';
import { ArrowLeft, UserX } from 'lucide-react';
import { useI18n } from '../i18n/context';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { LegalParagraphs } from './LegalText';

/** Публичная страница для Google Play Console: URL удаления аккаунта. */
export default function AccountDeletion({ onBack }) {
  const { t } = useI18n();

  useEffect(() => {
    if (typeof document !== 'undefined') {
      const prev = document.title;
      document.title = `${t('accountDeletion.title')} · MadCoach`;
      return () => {
        document.title = prev;
      };
    }
    return undefined;
  }, [t]);

  const handleBack = () => {
    if (typeof onBack === 'function') {
      onBack();
      return;
    }
    if (typeof window !== 'undefined') {
      if (window.history.length > 1) {
        window.history.back();
      } else {
        window.location.assign('/');
      }
    }
  };

  const openPrivacy = () => {
    try {
      window.dispatchEvent(new CustomEvent('mad:openPrivacy'));
    } catch {
      window.location.assign('/privacy');
    }
  };

  const steps = [
    t('accountDeletion.step1'),
    t('accountDeletion.step2'),
    t('accountDeletion.step3'),
    t('accountDeletion.step4'),
  ];

  return (
    <div className="relative min-h-[100dvh] min-h-screen w-full bg-gradient-to-b from-slate-50 to-white">
      <header className="sticky top-0 z-10 border-b border-slate-200/70 bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="mx-auto flex w-full max-w-3xl items-center gap-2 px-4 py-3 sm:px-6">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleBack}
            aria-label={t('accountDeletion.back')}
            className="h-9 w-9 shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex min-w-0 items-center gap-2">
            <UserX className="h-5 w-5 shrink-0 text-amber-700" />
            <span className="truncate text-sm font-medium text-slate-700">{t('accountDeletion.title')}</span>
          </div>
        </div>
      </header>

      <main id="account-deletion" className="mx-auto w-full max-w-3xl px-4 pb-20 pt-8 sm:px-6">
        <div className="mb-8">
          <div className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800">
            <UserX className="h-3.5 w-3.5" />
            MadCoach
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">{t('accountDeletion.title')}</h1>
          <p className="mt-2 text-sm text-slate-500">{t('accountDeletion.lastUpdated')}</p>
          <p className="mt-4 text-[15px] leading-relaxed text-slate-700">{t('accountDeletion.heroNote')}</p>
          <p className="mt-4 text-[15px] leading-relaxed text-slate-700">{t('accountDeletion.intro')}</p>
        </div>

        <Separator className="bg-slate-200" />

        <section className="mt-10 space-y-4" aria-labelledby="account-deletion-steps">
          <h2 id="account-deletion-steps" className="text-base font-semibold text-slate-900 sm:text-lg">
            {t('accountDeletion.stepsTitle')}
          </h2>
          <ol className="list-decimal space-y-4 pl-5 text-[15px] leading-relaxed text-slate-800">
            {steps.map((text, idx) => (
              <li key={`account-deletion-step-${idx}`}>{text}</li>
            ))}
          </ol>
        </section>

        <section className="mt-10 space-y-3" aria-labelledby="account-deletion-data">
          <h2 id="account-deletion-data" className="text-base font-semibold text-slate-900 sm:text-lg">
            {t('accountDeletion.dataTitle')}
          </h2>
          <LegalParagraphs text={t('accountDeletion.dataBody')} className="text-slate-700" />
        </section>

        <section className="mt-10 space-y-3" aria-labelledby="account-deletion-retention">
          <h2 id="account-deletion-retention" className="text-base font-semibold text-slate-900 sm:text-lg">
            {t('accountDeletion.retentionTitle')}
          </h2>
          <LegalParagraphs text={t('accountDeletion.retentionBody')} className="text-slate-700" />
        </section>

        <section className="mt-10 space-y-3" aria-labelledby="account-deletion-contact">
          <h2 id="account-deletion-contact" className="text-base font-semibold text-slate-900 sm:text-lg">
            {t('accountDeletion.contactTitle')}
          </h2>
          <LegalParagraphs text={t('accountDeletion.contactBody')} className="text-slate-700" />
        </section>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Button type="button" variant="link" className="h-auto px-2 text-[15px] text-sky-700" onClick={openPrivacy}>
            {t('accountDeletion.linkPrivacy')}
          </Button>
          <Button type="button" variant="outline" onClick={handleBack} className="min-w-32">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('accountDeletion.back')}
          </Button>
        </div>
      </main>
    </div>
  );
}
